import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductETA extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateETA);
    closestSection?.addEventListener(ThemeEvents.variantSelected, this.previewVariantName);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateETA);
    closestSection?.removeEventListener(ThemeEvents.variantSelected, this.previewVariantName);
  }

  /**
   * Renders the newly selected variant name as soon as it is clicked.
   *
   * `variantUpdate` only fires once the section has been re-fetched, so on pre-order
   * products the variant name in the ETA copy would visibly lag behind the picker.
   * This paints the new name immediately; `updateETA` then morphs in the authoritative
   * server-rendered copy a moment later.
   *
   * @param {Event} event - The variant selected event.
   */
  previewVariantName = (event) => {
    const target = this.querySelector('[data-eta-variant]');
    const picker = event.target;

    if (!target || !(picker instanceof HTMLElement)) return;

    // Guard against sibling products (combined listings, quick add) on the same section.
    const { productId } = picker.dataset;
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    // Same selector the variant picker uses to read its own selection, so joining the
    // values reproduces Shopify's `variant.title` format ("Queen / White").
    const selectedOptions = picker.querySelectorAll('select option[selected], fieldset input:checked');

    const title = Array.from(selectedOptions)
      .map((option) =>
        option instanceof HTMLInputElement || option instanceof HTMLOptionElement ? option.value : ''
      )
      .filter(Boolean)
      .join(' / ');

    if (title) target.textContent = title;
  };

  /**
   * Updates the ETA.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateETA = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newETA = event.detail.data.html.querySelector('product-ETA');

    if (!newETA) return;

    morph(this, newETA, { childrenOnly: true });
  };
}

if (!customElements.get('product-eta')) {
  customElements.define('product-eta', ProductETA);
}
